import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Store } from 'lucide-react';
import api from '../../../utils/api';
import { apiErrorMessage } from '../../../utils/apiError';
import { EnterpriseEndpoints } from '../../../utils/endpoints';
import { Permissions } from '../../../utils/permissions';
import { useAuth } from '../../../AuthContext';
import PageHeader from '../../shell/PageHeader';
import { Alert, Button, Card, EmptyState, PageLoader, StatCard, StatusPill } from '../../ui';
import TransactionsTable from './enterprise/TransactionsTable';
import PayoutDialog from './enterprise/PayoutDialog';
import EnterpriseDeactivated, { isEnterpriseDeactivated } from './enterprise/EnterpriseDeactivated';

const LIST_PATH = '/layout/enterprise-merchants';

const kpiMetrics = (kpis = {}) => [
  { label: 'Merchant Balance', value: kpis.balance, unit: 'currency' },
  { label: 'Total Tap to Pay Processed', value: kpis.total_processed, unit: 'currency' },
  { label: 'Processed Today (since 00:00 UTC)', value: kpis.processed_today, unit: 'currency' },
  { label: 'Discounted Transactions', value: kpis.discounted_count, unit: 'count' },
  { label: 'Discount Amount', value: kpis.discount_amount, unit: 'currency' },
  { label: 'Total Transactions', value: kpis.total_count, unit: 'count' },
];

const EnterpriseMerchantDetail = () => {
  const { merchantId } = useParams();
  const { hasPermission } = useAuth();
  const canPayOut =
    hasPermission(Permissions.enterprisePayoutsCreate) && hasPermission(Permissions.enterprisePayouts);

  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [notice, setNotice] = useState('');

  const latestRequest = useRef(0);

  const load = useCallback(async () => {
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    setLoadError(null);
    try {
      const { data } = await api.get(EnterpriseEndpoints.merchant(merchantId));
      if (latestRequest.current !== requestId) return;
      setStore(data.data);
    } catch (err) {
      if (latestRequest.current !== requestId) return;
      setLoadError(err);
    }
    setLoading(false);
  }, [merchantId]);

  const retryInitialLoad = () => {
    setLoading(true);
    load();
  };

  useEffect(() => {
    setLoading(true);
    setStore(null);
    load();
  }, [load]);

  const breadcrumbs = [
    { label: 'Home', to: '/layout' },
    { label: 'Merchants', to: LIST_PATH },
    { label: store?.name ?? 'Merchant' },
  ];

  if (loading) {
    return (
      <>
        <PageLoader />
        <PageHeader title="Merchant" breadcrumbs={breadcrumbs} />
      </>
    );
  }

  if (isEnterpriseDeactivated(loadError)) {
    return (
      <>
        <PageHeader title="Merchant" breadcrumbs={breadcrumbs} />
        <EnterpriseDeactivated />
      </>
    );
  }

  if (!store) {
    const notFound = loadError?.response?.status === 404;
    return (
      <>
        <PageHeader title="Merchant" breadcrumbs={breadcrumbs} />
        {notFound ? (
          <Card>
            <EmptyState
              icon={<Store className="h-10 w-10" aria-hidden="true" />}
              title="Merchant not found"
              message="It may have been detached from your enterprise."
              action={
                <Link to={LIST_PATH} className="btn btn-primary">
                  Back to merchants
                </Link>
              }
            />
          </Card>
        ) : (
          <Alert variant="danger">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span>{apiErrorMessage(loadError, 'We could not load this merchant. Please try again.')}</span>
              <Button size="sm" variant="outline-danger" onClick={retryInitialLoad}>
                Retry
              </Button>
            </div>
          </Alert>
        )}
      </>
    );
  }

  const handlePaidOut = (message) => {
    setNotice(message);
    load();
  };

  return (
    <>
      <PageHeader
        title={store.name}
        breadcrumbs={breadcrumbs}
        actions={
          canPayOut && (
            <Button variant="primary" onClick={() => setPayoutOpen(true)}>
              Pay out
            </Button>
          )
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <StatusPill status={store.status} />
        <span className="text-base text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor">
          {store.account_number}
        </span>
      </div>

      {notice && (
        <Alert variant="success" className="mb-4" onDismiss={() => setNotice('')}>
          {notice}
        </Alert>
      )}

      {loadError && (
        <Alert variant="danger" className="mb-4" onDismiss={() => setLoadError(null)}>
          {apiErrorMessage(loadError, 'We could not refresh this merchant. Please reload the page.')}
        </Alert>
      )}

      <div className="mb-6 grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
        {kpiMetrics(store.kpis).map((metric) => (
          <StatCard key={metric.label} metric={metric} />
        ))}
      </div>

      <TransactionsTable
        endpoint={EnterpriseEndpoints.merchantTransactions(store.id)}
        exportEndpoint={EnterpriseEndpoints.merchantTransactionsExport(store.id)}
      />

      {canPayOut && (
        <PayoutDialog
          isOpen={payoutOpen}
          onClose={() => setPayoutOpen(false)}
          store={{ id: store.id, name: store.name, account_number: store.account_number }}
          onPaidOut={handlePaidOut}
        />
      )}
    </>
  );
};

export default EnterpriseMerchantDetail;
